import React, { useCallback, useMemo } from 'react';
import { matchPath, NavLink, useHistory, useLocation } from 'react-router-dom';
import cx from 'classnames';
import { getAuthNavigationLink, transformNavigationLink } from 'helpers/customNavigationRules';
import { urlCanParse } from 'helpers/urlCanParse';
import PropTypes from 'prop-types';
import qs from 'query-string';

import { SiteConfigManager, useApplicationState } from '@atlas/core';
import { casinoRoutes, COMPONENT_TYPES } from '@atlas/routing';
import { Icon, ITEM_MORE, Label } from '@atlas/ui';

const { CASINO, CASINO_LIVE } = COMPONENT_TYPES;
const { game } = casinoRoutes;

import PromotionsCounter from 'src/common/PromotionsCounter';

export const HEADER_ITEMS_WITH_COUNTER = ['Promotions'];

export const HEADER_ITEM_COUNTER_COMPONENT = {
  Promotions: PromotionsCounter,
};

const HeaderMenuItemCore = ({
  authRequired = false,
  className,
  hasNativeLinks,
  id,
  isFake,
  isMoreActive = false,
  label,
  link,
  onClick,
  onClickMore,
  pathname,
  search,
  subpages,
}) => {
  const { authenticated } = useApplicationState();
  const history = useHistory();
  const isItemMore = id === ITEM_MORE;

  const hasSubpages = subpages?.length > 0;
  const realLink = transformNavigationLink(link);
  const active = !!matchPath(pathname, { path: realLink }) || isMoreActive;

  const isDetailSportOrCasinoPage = React.useMemo(() => {
    const parsedSearch = qs.parse(search);
    if (parsedSearch.e) return true;
    const splitPathName = pathname.split('/');
    if (!(splitPathName[3] && game === splitPathName[2])) return false;

    const casinoPath = SiteConfigManager.getComponentPath(CASINO, 'casino');
    const liveCasinoPath = SiteConfigManager.getComponentPath(CASINO_LIVE, 'live-casino');
    return casinoPath.slice(1) === splitPathName[1] || liveCasinoPath.slice(1) === splitPathName[1];
  }, [pathname, search]);

  const redirectPath = useCallback(() => {
    if (authRequired && !authenticated) {
      const finalPath = pathname === '/signup' ? link : getAuthNavigationLink(pathname, realLink);

      return finalPath;
    }

    return realLink;
  }, [realLink, authRequired, authenticated, pathname, link]);

  const handleLinkClick = useCallback(
    e => {
      if (active && !isDetailSportOrCasinoPage) {
        e.preventDefault();
      }
    },
    [active, isDetailSportOrCasinoPage],
  );

  const openSubmenu = e => {
    e.preventDefault();
    history.push(subpages[0].location);
  };

  const CounterComponent = useMemo(() => {
    if (!isFake && HEADER_ITEMS_WITH_COUNTER.includes(label)) {
      return HEADER_ITEM_COUNTER_COMPONENT[label];
    }

    return null;
  }, [isFake, label]);

  const itemClassName = cx('tab-link', className, {
    active,
    'tab-link-more': isItemMore,
  });

  const renderItem = useMemo(() => {
    if (urlCanParse(link) || hasNativeLinks) {
      const href = hasNativeLinks && hasSubpages ? subpages[0].location : link;
      const onClick = !hasNativeLinks && hasSubpages ? openSubmenu : handleLinkClick;

      return (
        <a
          className={itemClassName}
          href={href}
          onClick={onClick}
          rel="noreferrer"
          target={hasNativeLinks ? '_self' : '_blank'}
        >
          <Label message={label} />
          {CounterComponent && <CounterComponent />}
        </a>
      );
    }

    if (isItemMore) {
      return (
        <button className={itemClassName} onClick={onClickMore} type="button">
          <Icon name={`arrow-${isMoreActive ? 'up' : 'down'}`} />
          <Label message={ITEM_MORE} />
        </button>
      );
    }

    return (
      <NavLink
        activeClassName=""
        className={itemClassName}
        onClick={hasSubpages ? openSubmenu : handleLinkClick}
        to={redirectPath}
      >
        <Label message={label} />
        {CounterComponent && <CounterComponent />}
      </NavLink>
    );
  }, [
    CounterComponent,
    handleLinkClick,
    hasNativeLinks,
    hasSubpages,
    isItemMore,
    isMoreActive,
    itemClassName,
    label,
    link,
    onClickMore,
    redirectPath,
    subpages,
  ]);

  return (
    <div
      className="tab-item-wrapper"
      data-id={id}
      onClick={onClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
    >
      {renderItem}
    </div>
  );
};

HeaderMenuItemCore.propTypes = {
  authRequired: PropTypes.bool,
  className: PropTypes.string,
  hasNativeLinks: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isFake: PropTypes.bool,
  isMoreActive: PropTypes.bool,
  label: PropTypes.string,
  link: PropTypes.string,
  onClick: PropTypes.func,
  onClickMore: PropTypes.func,
  pathname: PropTypes.string,
  search: PropTypes.string,
  subpages: PropTypes.array,
};

const HeaderMenuItem = props => {
  const { pathname, search } = useLocation();
  return <HeaderMenuItemCore pathname={pathname} search={search} {...props} />;
};

export const NativeHeaderMenuItem = props => (
  <HeaderMenuItemCore
    pathname={window.location.pathname}
    search={window.location.search}
    {...props}
  />
);

export default React.memo(HeaderMenuItem);
